import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalFooterEnd } from '@modals/core-ui/ModalFooter/variations/ModalFooterEnd';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useEffect, useMemo, useRef, useState, type SyntheticEvent } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, type PercentCrop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { cropImageToDataUrl } from './helpers/cropImageToDataUrl';
import { scaleCropToNaturalPixels } from './helpers/scaleCropToNaturalPixels';
import { TProps } from './types/TProps';

const OUTPUT_SIZE = 400;
const INITIAL_CROP_PERCENT = 90;
const MIN_CROP_SIZE = 40;

export function ImageUploadModal({ file, onCancel, onSuccess }: TProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [crop, setCrop] = useState<PercentCrop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imageRef = useRef<HTMLImageElement>(null);

  const imageSrc = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => () => URL.revokeObjectURL(imageSrc), [imageSrc]);

  const onImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = event.currentTarget;

    setCrop(centerCrop(makeAspectCrop({ unit: '%', width: INITIAL_CROP_PERCENT }, 1, width, height), width, height));
  };

  const submit = async () => {
    const image = imageRef.current;

    if (!completedCrop || !image) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const area = scaleCropToNaturalPixels(completedCrop, {
      displayedWidth: image.width,
      displayedHeight: image.height,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
    });

    try {
      onSuccess(await cropImageToDataUrl(imageSrc, area, OUTPUT_SIZE));
    } catch {
      setError('We could not process that image. Please try another file.');
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper width="487px" onCancel={onCancel}>
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Crop your photo</ModalTitle>
      <div className="flex justify-center overflow-hidden rounded-md bg-surface-muted">
        <ReactCrop
          className="ReactCrop--no-animate max-h-96"
          crop={crop}
          aspect={1}
          keepSelection={true}
          minWidth={MIN_CROP_SIZE}
          minHeight={MIN_CROP_SIZE}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          onComplete={setCompletedCrop}
        >
          <img ref={imageRef} src={imageSrc} alt="" onLoad={onImageLoad} />
        </ReactCrop>
      </div>
      {error ? <p className="m-0 text-sm text-danger">{error}</p> : null}
      <ModalFooterEnd>
        <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
        <Button disabled={!completedCrop || isSubmitting} data-testid="ImageUploadModalSubmitButton" onClick={submit}>
          Save
        </Button>
      </ModalFooterEnd>
    </ModalWrapper>
  );
}
